import {useEffect, useRef} from "react";

import {authSucceeded} from "../../auth/actions/auth_succeeded_event";
import {emailSignUp} from "../../auth/api/email_sign_up";
import {AuthModalVariant} from "../../auth/components/auth_modal/AuthModalHeaderVariants";
import {closeFlow, startRegistrationFlow} from "../../auth/state/registration_flow_slice";
import {AuthActionType} from "../../auth/types/AuthActionType";
import {addNotification} from "../../notifications/state/notifications_slice";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {defaultAPIErrorNotification} from "../../notifications/utils/error_notifications";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {gtmEventApplicationRegister} from "../../tracking/google_tag_manager/gtm_event_application_register";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {getUserProfileApi, useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {usePatchUserProfileMutation} from "../../user/api/patch_user_profile";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {IUserProfile} from "../../user/types/IUserProfile";
import {UserAccountType} from "../../user/types/UserAccountType";
import {ApplicationModalStateEvent, IApplicationModalStateContext} from "../reducers/application_modal_state_transitions";
import {IApplicationModalState, IApplicationModalStateData} from "../reducers/application_modal_step_reducer";
import {setContinuePromotionFlowAfterAuth, setPromotionLeadDraft} from "../state/main_lead_slice";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationFormValues} from "../types/form/IApplicationFormValues";
import {IPromotionLeadDraft} from "../types/lead/IPromotionLeadDraft";
import {useSendMainLead} from "./use_send_main_lead";

interface IProps {
    handleModalClose: () => void;
    modalState: IApplicationModalState;
    sendModalEvent: (
        event: ApplicationModalStateEvent,
        dataUpdates?: Partial<IApplicationModalStateData>,
        contextUpdates?: Partial<IApplicationModalStateContext>
    ) => void;
}

export const usePromotionLeadFlow = (props: IProps) => {
    const {handleModalClose, modalState, sendModalEvent} = props;
    const dispatch = useAppDispatch();
    const sendLead = useSendMainLead();

    const mainLead = useAppSelector((state) => state.mainLead);
    const registrationFlow = useAppSelector((state) => state.registrationFlow);
    const isAuthenticated = useIsAuthenticated();
    const {data: userProfile} = useGetUserProfileQuery();
    const [patchUserProfile] = usePatchUserProfileMutation();

    const authFlowWasOpenRef = useRef(false);
    const startedAuthenticatedRef = useRef(false);
    const authActionDuringPromotionFlowRef = useRef<AuthActionType | null>(null);
    const registerEventSentRef = useRef(false);

    const syncDraftToUserProfile = async (draft: IPromotionLeadDraft, profile = userProfile, shouldSkipSync = false) => {
        if (shouldSkipSync) {
            return profile;
        }

        const draftPhone = `${draft.phone.code} ${draft.phone.number}`;
        const shouldPatchProfile = profile?.name !== draft.name || profile?.phone !== draftPhone;

        if (!shouldPatchProfile) {
            return profile;
        }

        await patchUserProfile({
            name: draft.name,
            phone: draftPhone
        }).unwrap();

        return await dispatch(getUserProfileApi.endpoints.getUserProfile.initiate(undefined, {forceRefetch: true})).unwrap();
    };

    const finalizePromotionLead = async (draft: IPromotionLeadDraft) => {
        sendModalEvent({type: "PROMOTION_SUBMIT_STARTED"});

        try {
            const application = await sendLead(
                {
                    name: draft.name,
                    email: draft.email,
                    phone: draft.phone,
                    marketing_email: draft.marketing_email,
                    marketing_sms: draft.marketing_sms,
                    additionalQuestions: draft.additionalQuestions
                },
                () => handleModalClose(),
                {persistFormValues: false}
            );

            if (
                application &&
                !application.user_created &&
                !startedAuthenticatedRef.current &&
                authActionDuringPromotionFlowRef.current === AuthActionType.REGISTRATION &&
                !registerEventSentRef.current
            ) {
                registerEventSentRef.current = true;
                gtmEventApplicationRegister();
            }

            const shouldShowPromotionSuccessModal = !startedAuthenticatedRef.current && authActionDuringPromotionFlowRef.current !== AuthActionType.LOGIN;

            if (!shouldShowPromotionSuccessModal) {
                handleModalClose();
                return;
            }

            sendModalEvent({type: "PROMOTION_SUBMIT_SUCCEEDED"}, {originalApplicationResponse: application || null});
        } catch {
            dispatch(addFailureNotification("Nie udało się wysłać zgłoszenia."));
            sendModalEvent({type: "PROMOTION_SUBMIT_FAILED"});
        }
    };

    const continuePromotionLeadAfterAuthentication = async (profile: IUserProfile) => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        try {
            if (profile?.phone_verified) {
                await finalizePromotionLead(draft);
                return;
            }

            await syncDraftToUserProfile(draft, profile, false);
            sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
        } catch (_error) {
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            sendModalEvent({type: "PROMOTION_SUBMIT_FAILED"});
        }
    };

    const handleDraftSubmit = async (
        values: Pick<IApplicationFormValues, "name" | "email" | "phone" | "marketing_email" | "marketing_sms" | "additionalQuestions">
    ) => {
        startedAuthenticatedRef.current = isAuthenticated;
        authActionDuringPromotionFlowRef.current = null;
        registerEventSentRef.current = false;

        const draft: IPromotionLeadDraft = {
            ...values,
            vendorId: mainLead.vendorId || 0,
            offerId: mainLead.offerId,
            propertyId: mainLead.propertyId,
            source: mainLead.source!,
            sourceSection: mainLead.sourceSection!
        };

        dispatch(setPromotionLeadDraft(draft));

        if (isAuthenticated && userProfile?.phone_verified) {
            await finalizePromotionLead(draft);
            return;
        }

        if (isAuthenticated) {
            try {
                await syncDraftToUserProfile(draft, userProfile);
                sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
            } catch (_error) {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
            }

            return;
        }

        try {
            await emailSignUp({
                email: draft.email,
                name: draft.name,
                phone: `${draft.phone.code} ${draft.phone.number}`,
                type: UserAccountType.LOCAL,
                source: UserSource.REGISTRATION,
                marketing_email: draft.marketing_email,
                marketing_sms: draft.marketing_sms
            });

            dispatch(
                authSucceeded({
                    type: AuthActionType.REGISTRATION,
                    userSource: UserSource.REGISTRATION,
                    registrationMethod: "email"
                })
            );
            authActionDuringPromotionFlowRef.current = AuthActionType.REGISTRATION;
            dispatch(setContinuePromotionFlowAfterAuth(true));
        } catch (_error) {
            dispatch(setContinuePromotionFlowAfterAuth(true));
            sendModalEvent({type: "PROMOTION_AUTH_REQUIRED"});
            dispatch(
                startRegistrationFlow({
                    userSource: UserSource.REGISTRATION,
                    modalVariant: "hidden_prices_variant_b",
                    titleVariant: AuthModalVariant.PROMOTION_LEAD,
                    showLoginForm: false
                })
            );
        }
    };

    const handlePhoneVerificationSuccess = async () => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        await finalizePromotionLead(draft);
    };

    const handlePhoneVerificationChangeNumber = () => {
        sendModalEvent({type: "PROMOTION_EDIT_PHONE"});
    };

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth) {
            authFlowWasOpenRef.current = false;
            return;
        }

        if (registrationFlow.isActive) {
            authFlowWasOpenRef.current = true;
            return;
        }

        if (!authFlowWasOpenRef.current || modalState.step !== ApplicationModalStep.PROMOTION_AUTH_FALLBACK || registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        dispatch(setContinuePromotionFlowAfterAuth(false));
        handleModalClose();
    }, [dispatch, handleModalClose, mainLead.continuePromotionFlowAfterAuth, modalState.step, registrationFlow.isActive, registrationFlow.lastAuthAction]);

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth || !isAuthenticated || !userProfile) {
            return;
        }

        if (modalState.step === ApplicationModalStep.PROMOTION_AUTH_FALLBACK && !registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        if (registrationFlow.lastAuthAction) {
            authActionDuringPromotionFlowRef.current = registrationFlow.lastAuthAction;
        }
        dispatch(setContinuePromotionFlowAfterAuth(false));
        if (registrationFlow.isActive) {
            dispatch(closeFlow());
        }
        void continuePromotionLeadAfterAuthentication(userProfile);
    }, [
        dispatch,
        isAuthenticated,
        mainLead.continuePromotionFlowAfterAuth,
        modalState.step,
        registrationFlow.isActive,
        registrationFlow.lastAuthAction,
        userProfile
    ]);

    return {
        handleDraftSubmit,
        handlePhoneVerificationSuccess,
        handlePhoneVerificationChangeNumber
    };
};
import {useEffect, useRef} from "react";

import {authSucceeded} from "../../auth/actions/auth_succeeded_event";
import {emailSignUp} from "../../auth/api/email_sign_up";
import {AuthModalVariant} from "../../auth/components/auth_modal/AuthModalHeaderVariants";
import {closeFlow, startRegistrationFlow} from "../../auth/state/registration_flow_slice";
import {AuthActionType} from "../../auth/types/AuthActionType";
import {addNotification} from "../../notifications/state/notifications_slice";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {defaultAPIErrorNotification} from "../../notifications/utils/error_notifications";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {gtmEventApplicationRegister} from "../../tracking/google_tag_manager/gtm_event_application_register";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {getUserProfileApi, useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {usePatchUserProfileMutation} from "../../user/api/patch_user_profile";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {IUserProfile} from "../../user/types/IUserProfile";
import {UserAccountType} from "../../user/types/UserAccountType";
import {ApplicationModalStateEvent, IApplicationModalStateContext} from "../reducers/application_modal_state_transitions";
import {IApplicationModalState, IApplicationModalStateData} from "../reducers/application_modal_step_reducer";
import {setContinuePromotionFlowAfterAuth, setPromotionLeadDraft} from "../state/main_lead_slice";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationFormValues} from "../types/form/IApplicationFormValues";
import {IPromotionLeadDraft} from "../types/lead/IPromotionLeadDraft";
import {useSendMainLead} from "./use_send_main_lead";

interface IProps {
    handleModalClose: () => void;
    modalState: IApplicationModalState;
    sendModalEvent: (
        event: ApplicationModalStateEvent,
        dataUpdates?: Partial<IApplicationModalStateData>,
        contextUpdates?: Partial<IApplicationModalStateContext>
    ) => void;
}

export const usePromotionLeadFlow = (props: IProps) => {
    const {handleModalClose, modalState, sendModalEvent} = props;
    const dispatch = useAppDispatch();
    const sendLead = useSendMainLead();

    const mainLead = useAppSelector((state) => state.mainLead);
    const registrationFlow = useAppSelector((state) => state.registrationFlow);
    const isAuthenticated = useIsAuthenticated();
    const {data: userProfile} = useGetUserProfileQuery();
    const [patchUserProfile] = usePatchUserProfileMutation();

    const authFlowWasOpenRef = useRef(false);
    const startedAuthenticatedRef = useRef(false);
    const authActionDuringPromotionFlowRef = useRef<AuthActionType | null>(null);
    const registerEventSentRef = useRef(false);

    const syncDraftToUserProfile = async (draft: IPromotionLeadDraft, profile = userProfile, shouldSkipSync = false) => {
        if (shouldSkipSync) {
            return profile;
        }

        const draftPhone = `${draft.phone.code} ${draft.phone.number}`;
        const shouldPatchProfile = profile?.name !== draft.name || profile?.phone !== draftPhone;

        if (!shouldPatchProfile) {
            return profile;
        }

        await patchUserProfile({
            name: draft.name,
            phone: draftPhone
        }).unwrap();

        return await dispatch(getUserProfileApi.endpoints.getUserProfile.initiate(undefined, {forceRefetch: true})).unwrap();
    };

    const finalizePromotionLead = async (draft: IPromotionLeadDraft) => {
        sendModalEvent({type: "PROMOTION_SUBMIT_STARTED"});

        try {
            const application = await sendLead(
                {
                    name: draft.name,
                    email: draft.email,
                    phone: draft.phone,
                    marketing_email: draft.marketing_email,
                    marketing_sms: draft.marketing_sms,
                    additionalQuestions: draft.additionalQuestions
                },
                () => handleModalClose(),
                {persistFormValues: false}
            );

            if (
                application &&
                !application.user_created &&
                !startedAuthenticatedRef.current &&
                authActionDuringPromotionFlowRef.current === AuthActionType.REGISTRATION &&
                !registerEventSentRef.current
            ) {
                registerEventSentRef.current = true;
                gtmEventApplicationRegister();
            }

            const shouldShowPromotionSuccessModal = !startedAuthenticatedRef.current && authActionDuringPromotionFlowRef.current !== AuthActionType.LOGIN;

            if (!shouldShowPromotionSuccessModal) {
                handleModalClose();
                return;
            }

            sendModalEvent({type: "PROMOTION_SUBMIT_SUCCEEDED"}, {originalApplicationResponse: application || null});
        } catch {
            dispatch(addFailureNotification("Nie udało się wysłać zgłoszenia."));
            sendModalEvent({type: "PROMOTION_SUBMIT_FAILED"});
        }
    };

    const continuePromotionLeadAfterAuthentication = async (profile: IUserProfile) => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        try {
            if (profile?.phone_verified) {
                await finalizePromotionLead(draft);
                return;
            }

            await syncDraftToUserProfile(draft, profile, false);
            sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
        } catch (_error) {
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            sendModalEvent({type: "PROMOTION_SUBMIT_FAILED"});
        }
    };

    const handleDraftSubmit = async (
        values: Pick<IApplicationFormValues, "name" | "email" | "phone" | "marketing_email" | "marketing_sms" | "additionalQuestions">
    ) => {
        startedAuthenticatedRef.current = isAuthenticated;
        authActionDuringPromotionFlowRef.current = null;
        registerEventSentRef.current = false;

        const draft: IPromotionLeadDraft = {
            ...values,
            vendorId: mainLead.vendorId || 0,
            offerId: mainLead.offerId,
            propertyId: mainLead.propertyId,
            source: mainLead.source!,
            sourceSection: mainLead.sourceSection!
        };

        dispatch(setPromotionLeadDraft(draft));

        if (isAuthenticated && userProfile?.phone_verified) {
            await finalizePromotionLead(draft);
            return;
        }

        if (isAuthenticated) {
            try {
                await syncDraftToUserProfile(draft, userProfile);
                sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
            } catch (_error) {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
            }

            return;
        }

        try {
            await emailSignUp({
                email: draft.email,
                name: draft.name,
                phone: `${draft.phone.code} ${draft.phone.number}`,
                type: UserAccountType.LOCAL,
                source: UserSource.REGISTRATION,
                marketing_email: draft.marketing_email,
                marketing_sms: draft.marketing_sms
            });

            dispatch(
                authSucceeded({
                    type: AuthActionType.REGISTRATION,
                    userSource: UserSource.REGISTRATION,
                    registrationMethod: "email"
                })
            );
            authActionDuringPromotionFlowRef.current = AuthActionType.REGISTRATION;
            dispatch(setContinuePromotionFlowAfterAuth(true));
        } catch (_error) {
            dispatch(setContinuePromotionFlowAfterAuth(true));
            sendModalEvent({type: "PROMOTION_AUTH_REQUIRED"});
            dispatch(
                startRegistrationFlow({
                    userSource: UserSource.REGISTRATION,
                    modalVariant: "hidden_prices_variant_b",
                    titleVariant: AuthModalVariant.PROMOTION_LEAD,
                    showLoginForm: false
                })
            );
        }
    };

    const handlePhoneVerificationSuccess = async () => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        await finalizePromotionLead(draft);
    };

    const handlePhoneVerificationChangeNumber = () => {
        sendModalEvent({type: "PROMOTION_EDIT_PHONE"});
    };

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth) {
            authFlowWasOpenRef.current = false;
            return;
        }

        if (registrationFlow.isActive) {
            authFlowWasOpenRef.current = true;
            return;
        }

        if (!authFlowWasOpenRef.current || modalState.step !== ApplicationModalStep.PROMOTION_AUTH_FALLBACK || registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        dispatch(setContinuePromotionFlowAfterAuth(false));
        handleModalClose();
    }, [dispatch, handleModalClose, mainLead.continuePromotionFlowAfterAuth, modalState.step, registrationFlow.isActive, registrationFlow.lastAuthAction]);

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth || !isAuthenticated || !userProfile) {
            return;
        }

        if (modalState.step === ApplicationModalStep.PROMOTION_AUTH_FALLBACK && !registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        if (registrationFlow.lastAuthAction) {
            authActionDuringPromotionFlowRef.current = registrationFlow.lastAuthAction;
        }
        dispatch(setContinuePromotionFlowAfterAuth(false));
        if (registrationFlow.isActive) {
            dispatch(closeFlow());
        }
        void continuePromotionLeadAfterAuthentication(userProfile);
    }, [
        dispatch,
        isAuthenticated,
        mainLead.continuePromotionFlowAfterAuth,
        modalState.step,
        registrationFlow.isActive,
        registrationFlow.lastAuthAction,
        userProfile
    ]);

    return {
        handleDraftSubmit,
        handlePhoneVerificationSuccess,
        handlePhoneVerificationChangeNumber
    };
};
